import { ReactNode } from "react";
import styled, { css } from "styled-components";
import {
  ColorProps,
  space,
  SpaceProps,
  TypographyProps,
  typography,
  color,
} from "styled-system";
import TableCell from "../TableCell/TableCell";
import useThemeColor from "../useThemeColor.hook";

interface TableRowProps extends SpaceProps, TypographyProps, ColorProps {
  /**
   * children elements
   */
  children: ReactNode;
  /**
   *The element's unique identifier
   */
  id?: string;
  /**
   * highlight at the begining of the row
   */
  highlight?: boolean;
  /**
   * highlight's color
   */
  highlightColor?: string;
  /**
   * automatically set, please don't use it
   */
  autoNumbering?: boolean;
  /**
   * automatically set, please don't use it
   */
  rowNumber?: number;
}

const Row = styled.tr<TableRowProps>`
  border-bottom: 1px solid #e1e4e7;
  vertical-align: top;
  position: relative;
  font-family: inherit;
  font-weight: inherit;
  font-size: inherit;
  padding: inherit;
  margin: inherit;

  &.highlight {
    td:first-child:after {
      position: absolute;
      top: 0;
      left: 0;
      width: 5px;
      height: 100%;
      content: "";
      ${(props) => css({ backgroundColor: props.highlightColor })}
    }
  }

  ${space}
  ${typography}
  ${color}
`;

const TableRow = ({
  children,
  highlight = false,
  highlightColor = "table-row-highlight-color",
  rowNumber,
  autoNumbering = false,
  color,
  ...props
}: TableRowProps) => {
  const hexColor = useThemeColor(highlightColor);
  const numberToDisplay = rowNumber ? `${rowNumber}.` : rowNumber;

  return (
    <Row
      className={highlight ? "highlight" : ""}
      highlightColor={hexColor}
      tabIndex={0}
      color={color as string}
      {...props}
    >
      {autoNumbering && (
        <TableCell
          aria-label={rowNumber ? `row ${rowNumber}` : "row number"}
          as={!rowNumber ? "th" : undefined}
          pr={0}
        >
          {numberToDisplay}
        </TableCell>
      )}
      {children}
    </Row>
  );
};

export default TableRow;
